/**
 * Setting up a workforce account.
 *
 * The activation API has existed and been complete for some time — issue,
 * consume, password, mfa/begin, mfa/confirm — and no file in this frontend
 * called any of it. So the only way to activate anyone was an administrator
 * reading a code off a terminal and relaying it by hand, which is why
 * twenty-six accounts were still waiting.
 *
 * Three steps, because the server enforces three: exchange the code for a
 * ticket, set a password, register an authenticator. The ticket is short-lived
 * and lives only in component state — never localStorage, because a bootstrap
 * credential that outlives the tab is a credential left lying around.
 *
 * A code issued to an account that already has a password resumes at the
 * authenticator step, and the server asks for that password alongside the
 * code (ACTIVATION_PASSWORD_REQUIRED) — this is how someone whose setup
 * stalled, or whose authenticator an administrator reset, gets back in.
 *
 * The invitation link fills the form but does not submit it. It used to, and
 * whichever browser opened the link first spent the code: every other tab or
 * device then read "You have already used this code" (2026-09-16, seventeen
 * times for one person in ten minutes).
 */
import React, { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle2, Loader2, ShieldCheck, KeyRound } from 'lucide-react';
import { apiRequest } from '../services/api';
import { TENANT } from '../config';
import BackupCodesPanel from '../components/Auth/BackupCodesPanel';
import { useSsoConfig } from '../services/ssoApi';

type Step = 'code' | 'password' | 'authenticator' | 'done' | 'stuck';

const MIN_PASSWORD = 12;

/**
 * The ticket is gone — expired, or already spent.
 *
 * Nothing on this page can recover from it: every remaining step authenticates
 * with that ticket, so re-pressing any button returns the same 401 forever.
 * The old code set an error banner and left the form standing, which is how a
 * user ended up holding a button that could only fail.
 *
 * Keyed on the decision code rather than the status: 401 alone would also
 * match a refresh-token race, and the code says exactly which thing happened.
 */
const ticketGone = (r: { code?: string }): boolean => r.code === 'ACTIVATION_TICKET_INVALID';

export default function Activate() {
  const [params] = useSearchParams();
  const [step, setStep] = useState<Step>('code');
  const [email, setEmail] = useState(params.get('email') || '');
  const [code, setCode] = useState(params.get('code') || '');
  const [ticket, setTicket] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [totp, setTotp] = useState('');
  const [qr, setQr] = useState('');
  const [secret, setSecret] = useState('');
  // Resuming: the account already has a password, and the server wants it
  // with the code. Shown only after the server has said so.
  const [needsCurrentPassword, setNeedsCurrentPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const fromLink = Boolean(params.get('email') && params.get('code'));
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [codesSaved, setCodesSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const [passwordSet, setPasswordSet] = useState(false);

  // Present only when Microsoft sign-in is on; the completion step then says how to sign in.
  const sso = useSsoConfig();

  const brand = TENANT.branding.name;
  const support = TENANT.branding.supportEmail;

  /** Stop offering actions that cannot succeed, and say what is true instead. */
  const enterStuck = useCallback(() => { setError(''); setStep('stuck'); }, []);

  /**
   * Start the authenticator step. Separate from the password write on purpose:
   * they used to share one handler, so a transient failure here left the user
   * on the password form with the password already saved — and that form's
   * only button could then return nothing but "already done", which is the
   * same dead end by a different route.
   */
  const beginMfa = useCallback(async (t: string): Promise<boolean> => {
    const m = await apiRequest<{ qr: string; secret: string }>('POST', '/activation/mfa/begin', { ticket: t });
    if (m.success && m.data) { setQr(m.data.qr); setSecret(m.data.secret); setError(''); return true; }
    if (ticketGone(m)) { enterStuck(); return false; }
    // Deliberately does not repeat "your password is saved" — the panel below
    // this banner already says it, and one screen should not tell the reader
    // the same reassurance twice in two different voices.
    setError(m.status === undefined
      ? 'Could not reach the server.'
      : (m.message || m.error || 'The authenticator step could not be started.'));
    return false;
  }, [enterStuck]);

  const submitCode = useCallback(async (e?: React.FormEvent) => {
    e?.preventDefault();
    setBusy(true); setError('');
    const r = await apiRequest<{ ticket: string; nextStep: string }>(
      'POST', '/activation/consume', {
        email: email.trim(),
        code: code.trim(),
        ...(needsCurrentPassword ? { password: currentPassword } : {}),
      }
    );
    if (!r.success || !r.data) {
      if (r.code === 'ACTIVATION_PASSWORD_REQUIRED') {
        // Not a failure: the code was right and has not been used. Ask for
        // the one thing still missing.
        setNeedsCurrentPassword(true);
        setBusy(false);
        return;
      }
      // The server deliberately does not say which part was wrong.
      setError(r.status === undefined
        ? 'Could not reach the server. Check your connection and try again.'
        : (r.message || r.error || 'That code was not accepted. Check the address and code, or ask for a new one.'));
      setBusy(false);
      return;
    }
    const t = r.data.ticket;
    setTicket(t);
    setCurrentPassword('');
    if (r.data.nextStep === 'ENROL_AUTHENTICATOR') {
      // Resumed: the password already exists and cannot be changed here.
      setPasswordSet(true);
      setStep('authenticator');
      await beginMfa(t);
    } else {
      setStep('password');
    }
    setBusy(false);
  }, [email, code, needsCurrentPassword, currentPassword, beginMfa]);

  const submitPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirm) { setError('The two passwords do not match.'); return; }
    if (password.length < MIN_PASSWORD) {
      setError(`Use at least ${MIN_PASSWORD} characters.`); return;
    }
    setBusy(true); setError('');
    const r = await apiRequest('POST', '/activation/password', { ticket, password });
    if (!r.success) {
      if (ticketGone(r)) { enterStuck(); setBusy(false); return; }
      setError(r.message || r.error || 'That password was not accepted.');
      setBusy(false);
      return;
    }
    // The password is saved from here on. Advance even if the next call fails:
    // this form can no longer succeed, because the server refuses a second
    // password for the same ticket.
    setPasswordSet(true);
    setStep('authenticator');
    await beginMfa(ticket);
    setBusy(false);
  };

  const submitTotp = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setError('');
    const r = await apiRequest<{ activated: boolean; backupCodes?: string[] }>(
      'POST', '/activation/mfa/confirm', { ticket, code: totp.trim() }
    );
    if (!r.success || !r.data) {
      if (ticketGone(r)) { enterStuck(); setBusy(false); return; }
      setError(r.message || r.error || 'That six-digit code was not accepted. Try the next one your app shows.');
      setBusy(false);
      return;
    }
    setBackupCodes(r.data.backupCodes || []);
    setStep('done');
    setBusy(false);
  };

  const stepIndex = { code: 0, password: 1, authenticator: 2, done: 3, stuck: 2 }[step];

  return (
    <div className="min-h-screen bg-[#f6faf9] px-4 py-10 text-[#10252d]">
      <div className="mx-auto w-full max-w-lg">
        <div className="mb-7 flex items-center gap-3">
          <img src={TENANT.branding.logoPrimaryPath} alt={brand} className="max-h-9 max-w-[150px] object-contain" />
          <span className="text-sm font-medium text-[#60747b]">Account setup</span>
        </div>

        <ol className="mb-6 flex items-center gap-2 text-xs font-medium text-[#60747b]">
          {['Code', 'Password', 'Authenticator'].map((label, i) => (
            <li key={label} className="flex flex-1 items-center gap-2">
              <span
                className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold ${
                  i < stepIndex ? 'bg-[#17685d] text-white'
                    : i === stepIndex ? 'bg-[#10252d] text-white' : 'bg-[#d7e5e1] text-[#60747b]'
                }`}
              >
                {i < stepIndex ? '✓' : i + 1}
              </span>
              <span className={i === stepIndex ? 'text-[#10252d]' : undefined}>{label}</span>
              {i < 2 && <span className="h-px flex-1 bg-[#d7e5e1]" />}
            </li>
          ))}
        </ol>

        <div className="rounded-lg border border-[#d7e5e1] bg-white p-6 shadow-sm">
          {error && (
            <div className="mb-5 rounded-md border border-[#e8c9c9] bg-[#fbeeee] px-4 py-3 text-sm text-[#b94343]">
              {error}
            </div>
          )}

          {step === 'code' && (
            <form onSubmit={submitCode} className="flex flex-col gap-4">
              <div>
                <h1 className="text-xl font-semibold">Set up your {brand} account</h1>
                <p className="mt-2 text-sm leading-6 text-[#60747b]">
                  {fromLink
                    ? 'Your email and code are filled in from the link.'
                    : 'Enter the code from your invitation email.'}
                </p>
                <p className="mt-2 text-sm leading-6 text-[#60747b]">
                  Each code works once. Continue on the computer where you will
                  finish, and complete every step without closing this page.
                </p>
              </div>
              <label className="text-sm font-medium">
                Work email
                <input
                  required type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  autoComplete="username"
                  className="mt-1 h-11 w-full rounded-md border border-[#d7e5e1] px-3 text-sm outline-none focus:border-[#17685d]"
                />
              </label>
              <label className="text-sm font-medium">
                Activation code
                <input
                  required value={code} onChange={(e) => setCode(e.target.value.toUpperCase())}
                  placeholder="XXXXX-XXXXX" spellCheck={false}
                  className="mt-1 h-11 w-full rounded-md border border-[#d7e5e1] px-3 font-mono text-sm tracking-wider outline-none focus:border-[#17685d]"
                />
              </label>
              {needsCurrentPassword && (
                <div className="flex flex-col gap-3 rounded-md border border-[#d7e5e1] bg-[#f6faf9] p-4" role="status">
                  <p className="text-sm leading-6 text-[#10252d]">
                    <strong>This account already has a password.</strong> Enter it to
                    continue to the authenticator step. You will not be asked to
                    choose a new one.
                  </p>
                  <label className="text-sm font-medium">
                    Your {brand} password
                    <input
                      required type="password" value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      autoComplete="current-password" autoFocus
                      className="mt-1 h-11 w-full rounded-md border border-[#d7e5e1] bg-white px-3 text-sm outline-none focus:border-[#17685d]"
                    />
                  </label>
                  <p className="text-xs leading-5 text-[#60747b]">
                    Forgotten it? Ask your administrator to restart your setup
                    {support ? <> ({support})</> : null}. This page cannot reset it.
                  </p>
                </div>
              )}
              <button
                type="submit" disabled={busy}
                className="mt-1 inline-flex h-11 items-center justify-center gap-2 rounded-md bg-[#10252d] px-5 text-sm font-semibold text-white disabled:opacity-60"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
                Continue
              </button>
            </form>
          )}

          {step === 'password' && (
            <form onSubmit={submitPassword} className="flex flex-col gap-4">
              <div>
                <h1 className="text-xl font-semibold">Choose a password</h1>
                <p className="mt-2 text-sm leading-6 text-[#60747b]">
                  At least {MIN_PASSWORD} characters. Longer is better than complicated.
                </p>
              </div>
              <label className="text-sm font-medium">
                Password
                <input
                  required type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password" minLength={MIN_PASSWORD}
                  className="mt-1 h-11 w-full rounded-md border border-[#d7e5e1] px-3 text-sm outline-none focus:border-[#17685d]"
                />
              </label>
              <label className="text-sm font-medium">
                Confirm password
                <input
                  required type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                  autoComplete="new-password"
                  className="mt-1 h-11 w-full rounded-md border border-[#d7e5e1] px-3 text-sm outline-none focus:border-[#17685d]"
                />
              </label>
              <button
                type="submit" disabled={busy}
                className="mt-1 inline-flex h-11 items-center justify-center gap-2 rounded-md bg-[#10252d] px-5 text-sm font-semibold text-white disabled:opacity-60"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Set password
              </button>
            </form>
          )}

          {step === 'authenticator' && (
            <form onSubmit={submitTotp} className="flex flex-col gap-4">
              <div>
                <h1 className="text-xl font-semibold">Register your authenticator</h1>
                <p className="mt-2 text-sm leading-6 text-[#60747b]">
                  Scan this with Microsoft Authenticator, Google Authenticator, or
                  any TOTP app, then enter the six digits it shows.
                </p>
              </div>
              {!qr && (
                <div className="rounded-md border border-[#e8dcc0] bg-[#fdf4e6] p-4 text-sm leading-6 text-[#10252d]">
                  <p><strong>Your password is saved.</strong> The authenticator step did not start, which is a problem reaching the server — not a problem with your account or your password.</p>
                  <button
                    type="button" disabled={busy} onClick={() => beginMfa(ticket)}
                    className="mt-3 inline-flex h-9 items-center gap-1.5 rounded border border-[#d7e5e1] bg-white px-3 text-xs font-semibold disabled:opacity-60"
                  >
                    Try again
                  </button>
                </div>
              )}
              {qr && (
                <img
                  src={qr} alt="Authenticator setup code"
                  className="mx-auto h-44 w-44 rounded-md border border-[#d7e5e1] bg-white p-2"
                />
              )}
              {secret && (
                <p className="text-center text-xs text-[#60747b]">
                  Can&apos;t scan? Enter this key manually:<br />
                  <span className="font-mono text-[11px] tracking-wider text-[#10252d]">{secret}</span>
                </p>
              )}
              <label className="text-sm font-medium">
                Six-digit code
                <input
                  required value={totp} onChange={(e) => setTotp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  inputMode="numeric" autoComplete="one-time-code" placeholder="000000"
                  className="mt-1 h-11 w-full rounded-md border border-[#d7e5e1] px-3 text-center font-mono text-lg tracking-[0.3em] outline-none focus:border-[#17685d]"
                />
              </label>
              <button
                type="submit" disabled={busy || totp.length !== 6 || !qr}
                className="mt-1 inline-flex h-11 items-center justify-center gap-2 rounded-md bg-[#10252d] px-5 text-sm font-semibold text-white disabled:opacity-60"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
                Finish setup
              </button>
            </form>
          )}

          {step === 'stuck' && (
            <div className="flex flex-col gap-4" role="alert">
              <div>
                <h1 className="text-xl font-semibold">This setup link has expired</h1>
                <p className="mt-2 text-sm leading-6 text-[#60747b]">
                  Setup links are short-lived, and this one ran out before the last
                  step was finished. Nothing is wrong with your account.
                </p>
              </div>

              <div className="rounded-md border border-[#d7e5e1] bg-[#f6faf9] px-4 py-3 text-sm leading-6">
                <p className="font-semibold text-[#10252d]">Where things stand</p>
                <ul className="mt-1 list-disc pl-5 text-[#60747b]">
                  <li>{passwordSet
                    ? 'The password you chose was saved. You do not need to pick a new one.'
                    : 'No password was saved, so nothing was changed.'}</li>
                  <li>Your authenticator was not registered, and signing in needs it.</li>
                </ul>
              </div>

              <div>
                <p className="text-sm leading-6 text-[#10252d]">
                  <strong>What to do next.</strong> Ask for a new setup link — this page
                  cannot issue one, and trying again here will not help. The new link
                  picks up where you left off
                  {passwordSet ? '; you will need the password you chose' : ''}.
                </p>
                <p className="mt-2 text-sm leading-6 text-[#60747b]">
                  Your administrator can send one. If you are not sure who that is,
                  write to{' '}
                  <a className="font-medium text-[#17685d] underline" href={`mailto:${support}`}>{support}</a>
                  {' '}from your work address and say that your {brand} setup link expired.
                </p>
              </div>

              <p className="rounded-md bg-[#f6faf9] px-4 py-3 text-xs leading-5 text-[#60747b]">
                Do not create a second account. The one waiting for you keeps the
                access your role has already been approved for.
              </p>
            </div>
          )}

          {step === 'done' && (
            <div className="flex flex-col gap-4">
              <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[#e9f3f0]">
                <CheckCircle2 className="h-6 w-6 text-[#17685d]" />
              </div>
              <div>
                <h1 className="text-xl font-semibold">Your account is active</h1>
                <p className="mt-2 text-sm leading-6 text-[#60747b]">
                  You can sign in with your work email, your password, and your authenticator app.
                </p>
                {sso && (
                  <p className="mt-2 text-sm leading-6 text-[#60747b]">
                    From now on, sign in with &quot;Sign in with Microsoft&quot;.
                  </p>
                )}
              </div>

              {backupCodes.length > 0 && (
                <BackupCodesPanel codes={backupCodes} onSaved={() => setCodesSaved(true)} />
              )}

              <div className="rounded-md border border-[#d7e5e1] bg-[#f6faf9] p-4">
                <p className="text-sm font-semibold text-[#10252d]">If you lose or replace your phone</p>
                <p className="mt-1 text-xs leading-5 text-[#60747b]">
                  Your password alone will not sign you in. Use a backup code on the
                  sign-in page to register the new phone. If you have none left, ask
                  your administrator to reset your authenticator
                  {support ? <> ({support})</> : null}; you keep your password and your access.
                </p>
              </div>

              <p className="rounded-md bg-[#f6faf9] px-4 py-3 text-xs leading-5 text-[#60747b]">
                What you can see depends on the access your role has been approved for.
                If something you expect is missing, ask an administrator to check your
                role assignment rather than requesting a new account.
              </p>

              {backupCodes.length > 0 && !codesSaved ? (
                <button
                  type="button" disabled
                  className="inline-flex h-11 items-center justify-center rounded-md bg-[#10252d] px-5 text-sm font-semibold text-white opacity-60"
                >
                  Store your backup codes to continue
                </button>
              ) : (
                <a
                  href="/login"
                  className="inline-flex h-11 items-center justify-center rounded-md bg-[#10252d] px-5 text-sm font-semibold text-white"
                >
                  Sign in
                </a>
              )}
            </div>
          )}
        </div>

        <p className="mt-5 text-center text-xs text-[#60747b]">
          Didn&apos;t expect this? Nothing happens until you finish setup — you can
          ignore the email, and the code expires on its own.
        </p>
      </div>
    </div>
  );
}
